import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Proposal } from 'app/entities/proposal';
import { formatBRL } from 'app/helpers/money';
import {
    PaymentMethodDescribed,
    PaymentPlan,
    describePlan,
    detailPlan,
    optionNote,
    paymentMethodHeadline,
    paymentMethodMeta,
    paymentMethodSummary,
    paymentOptions,
} from 'app/helpers/payment-plan';
import { OaInstallmentOption } from 'app/main/orca-components/ds/oa-installment-picker.component';
import { ProposalService } from 'app/main/pages/proposals/proposals.service';

export interface ProposalApproveDialogResult {
    date: Date;
    payment_method?: string;
    installments?: number;
}

interface MethodOption {
    method: PaymentMethodDescribed;
    icon: string;
    title: string;
    description: string;
    value: string;
    plans: PaymentPlan[];
    picker: OaInstallmentOption[];
}

/**
 * ORCA-1932: aprovar no sistema. Data de aprovação obrigatória e, no formato novo com formas oferecidas,
 * a forma e as parcelas também (o botão Aprovar fica desabilitado até escolher).
 */
@Component({
    selector: 'app-proposal-approve-dialog',
    templateUrl: './proposal-approve-dialog.component.html',
    styleUrls: ['./proposal-approve-dialog.component.scss'],
})
export class ProposalApproveDialogComponent implements OnInit {
    proposal: Proposal;
    loading = false;
    date: Date | null = new Date();
    readonly today = new Date();
    options: MethodOption[] = [];
    selected: MethodOption | null = null;
    installments: number | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { proposal: Proposal },
        private dialogRef: MatDialogRef<ProposalApproveDialogComponent, ProposalApproveDialogResult>,
        private proposalService: ProposalService,
    ) {
        this.proposal = data.proposal;
    }

    ngOnInit(): void {
        // Nas listas o orçamento vem sem as formas: busca o detalhe
        if (this.proposal?.paymentFormat === undefined && this.proposal?.token) {
            this.loading = true;
            this.proposalService.find(this.proposal.token).subscribe(
                (proposal) => {
                    this.proposal = proposal;
                    this.buildOptions();
                    this.loading = false;
                },
                () => {
                    this.loading = false;
                }
            );
        } else {
            this.buildOptions();
        }
    }

    get total(): number {
        return Number(this.proposal?.totalLiquid) || 0;
    }

    get requiresMethod(): boolean {
        return this.options.length > 0;
    }

    get number(): string {
        return this.proposal?.proposalNumber ? `#${this.proposal.proposalNumber}` : '';
    }

    get clientName(): string {
        return this.proposal?.client?.name || 'o cliente';
    }

    get chosenPlan(): PaymentPlan | null {
        if (!this.selected || !this.installments) {
            return null;
        }
        return this.selected.plans.find((p) => p.installments === this.installments) ?? null;
    }

    get chosenTitle(): string {
        const plan = this.chosenPlan;
        return plan ? describePlan(plan) : '';
    }

    get chosenDetail(): string {
        const plan = this.chosenPlan;
        return plan ? detailPlan(plan) : '';
    }

    get missing(): string | null {
        const noDate = !this.date;
        const noMethod = this.requiresMethod && !this.chosenPlan;
        if (noDate && noMethod) {
            return 'Escolha a data de aprovação e a forma de pagamento para aprovar.';
        }
        if (noDate) {
            return 'Escolha a data de aprovação para aprovar.';
        }
        if (noMethod) {
            return 'Escolha a forma de pagamento para aprovar.';
        }
        return null;
    }

    pick(option: MethodOption) {
        if (this.selected !== option) {
            this.selected = option;
            this.installments = option.plans.length === 1 ? 1 : null;
        }
    }

    money(value: number): string {
        return formatBRL(value);
    }

    approve() {
        if (this.missing || this.loading) {
            return;
        }
        const result: ProposalApproveDialogResult = { date: this.date! };
        if (this.requiresMethod && this.selected && this.installments) {
            result.payment_method = this.selected.method.method;
            result.installments = this.installments;
        }
        this.dialogRef.close(result);
    }

    private buildOptions() {
        const methods = this.proposal?.paymentFormat === 'methods' && this.proposal?.hasInstallments
            ? (this.proposal.paymentMethods ?? [])
            : [];
        const base = this.total;

        this.options = methods.map((method) => {
            const plans = method.options?.length ? method.options : paymentOptions(base, method);
            return {
                method,
                icon: paymentMethodMeta(method.method)?.icon ?? 'payments',
                title: method.label || paymentMethodMeta(method.method)?.label || method.method,
                description: paymentMethodSummary(base, method),
                value: paymentMethodHeadline(base, method),
                plans,
                picker: plans.map((plan) => ({
                    n: plan.installments,
                    amount: formatBRL(plan.installment_value),
                    note: optionNote(plan),
                    free: plan.interest_amount <= 0,
                })),
            };
        });

        if (this.options.length === 1) {
            this.pick(this.options[0]);
        }
    }
}
